<script>
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

import { EDITOR_TOOLS } from './constants';
import {
  isNodeActive,
  isToolActive,
  isToolEnabled,
  selectionSpansLines,
} from './commands';

/**
 * Barra de ferramentas do editor.
 *
 * A barra antiga vinha do `prosemirror-menu`, dentro do node_modules: os
 * tooltips eram strings fixas em ingles ("Toggle strong style") e o DOM ficava
 * fora do alcance do design system. Aqui e um componente Vue comum, com
 * traducao e os mesmos icones do resto da aplicacao.
 *
 * A barra so desenha; nao executa. O EditorView nasce no `mounted` do pai — um
 * ciclo depois do primeiro render — entao, se a barra dependesse dele por prop,
 * o primeiro clique cairia num valor nulo. Ela emite a ferramenta escolhida e
 * quem executa e o pai, que sempre tem a view viva em maos.
 */
export default {
  name: 'RichTextEditorToolbar',
  components: { DashboardIcon },
  props: {
    /** EditorState atual, usado para desenhar o estado de cada botao. */
    editorState: { type: Object, default: null },
    /** Nomes das ferramentas a exibir, na ordem. */
    tools: { type: Array, default: () => [] },
  },
  computed: {
    visibleTools() {
      if (!this.editorState) return [];

      return this.tools
        .map(name => ({ name, ...EDITOR_TOOLS[name] }))
        .filter(tool => this.isSupported(tool));
    },
    /**
     * Quebra a barra em grupos por familia — formatacao, link, historico,
     * listas — para render um separador entre eles. Nove botoes numa fileira
     * unica ficam indistinguiveis; o agrupamento da onde mirar.
     */
    toolGroups() {
      const grupos = [];

      this.visibleTools.forEach(tool => {
        const anterior = grupos[grupos.length - 1];

        if (anterior && anterior[0].group === tool.group) {
          anterior.push(tool);
          return;
        }

        grupos.push([tool]);
      });

      return grupos;
    },
  },
  methods: {
    /** Esconde ferramentas cujo mark ou node nao existe no schema em uso. */
    isSupported(tool) {
      if (!tool.kind) return false;

      const { schema } = this.editorState;

      if (tool.kind === 'mark') return !!schema.marks[tool.mark];
      if (tool.kind === 'node') return !!schema.nodes[tool.node];

      return true;
    },
    isActive(tool) {
      return isToolActive(tool, this.editorState);
    },
    isEnabled(tool) {
      return !!isToolEnabled(tool, this.editorState);
    },
    label(tool) {
      return this.$t(`RICH_TEXT_EDITOR.${this.labelKey(tool)}`);
    },
    labelKey(tool) {
      if (tool.action === 'link' && this.isActive(tool)) return 'LINK_REMOVE';

      // Codigo muda de nome conforme a forma em jogo: sem quebra e "Codigo",
      // com quebra e "Monoespacado". Vale tanto para o que ja esta aplicado
      // quanto para o que a selecao atual produziria.
      if (tool.action === 'code' && this.isMonospace()) return 'CODE_BLOCK';

      return tool.i18n;
    },
    isMonospace() {
      const state = this.editorState;

      return (
        isNodeActive(state, state.schema.nodes.code_block) ||
        selectionSpansLines(state)
      );
    },
  },
};
</script>

<template>
  <div
    v-if="visibleTools.length"
    class="rich-text-toolbar"
    role="toolbar"
    :aria-label="$t('RICH_TEXT_EDITOR.TOOLBAR_LABEL')"
  >
    <template v-for="(group, index) in toolGroups">
      <span
        v-if="index > 0"
        :key="`sep-${index}`"
        class="rich-text-toolbar__separator"
        aria-hidden="true"
      />
      <button
        v-for="tool in group"
        :key="tool.name"
        v-tooltip.top="label(tool)"
        type="button"
        class="rich-text-toolbar__button"
        :class="{ 'is-active': isActive(tool) }"
        :disabled="!isEnabled(tool)"
        :aria-label="label(tool)"
        :aria-pressed="isActive(tool) ? 'true' : 'false'"
        tabindex="-1"
        @mousedown.prevent
        @click="$emit('run', tool)"
      >
        <DashboardIcon :icon="tool.icon" size="18" />
      </button>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.rich-text-toolbar {
  // Sem preenchimento proprio: a regua inferior sozinha ja fecha a area de
  // digitacao, e o fundo tingido competia com o resto do composer.
  //
  // `--composer-border` vem do .reply-box e carrega a identidade do modo, de
  // modo que a regua fica ambar numa nota interna. Fora do composer o token
  // nao existe e o fallback assume.
  @apply flex items-center gap-1 flex-wrap px-2;

  border-bottom: 1px solid var(--composer-border, theme('colors.line'));
}

.rich-text-toolbar__separator {
  @apply w-px h-5 mx-1 shrink-0 bg-line;
}

.rich-text-toolbar__button {
  // 32px de alvo com icone de 18. Antes eram 28 com icone de 16, e o icone
  // ficava pequeno demais para o botao — perdia presenca, principalmente no
  // tema claro.
  @apply flex items-center justify-center w-8 h-8 shrink-0 rounded-md text-body
    bg-transparent border-0 cursor-pointer transition-colors duration-100;

  &:hover:not(:disabled) {
    @apply bg-surface-hover text-brand-text;
  }

  &:active:not(:disabled) {
    @apply bg-line-faint;
  }

  // Estado ligado usa o tint da marca em vez de `surface-hover`. No tema claro
  // `surface-hover` e slate-50 sobre branco: a diferenca era pequena demais
  // para dizer se o negrito estava ou nao aplicado.
  &.is-active {
    @apply bg-brand-subtle text-brand-text;

    &:hover {
      @apply bg-brand-subtle text-brand-text;
    }
  }

  &:focus-visible {
    @apply outline-none ring-2 ring-brand/40 ring-offset-2 ring-offset-surface;
  }

  &:disabled {
    // Menos apagado que antes: a 40% os botoes de desfazer/refazer sumiam.
    @apply opacity-50 cursor-default text-subtle;
  }
}
</style>
